"use client";

/**
 * LoadingOverlay Component
 *
 * Full-screen loading overlay shown during account analysis.
 * Features:
 * - Outfame logo mark + user avatar partnership display
 * - Preply-style morphing status messages
 * - Clean, minimal design worthy of a premium SaaS
 * - Smooth fade in/out transitions
 *
 * This is an intermediary step that doesn't affect the step indicator.
 * It overlays the entire screen during the transition from Username → Analysis.
 */
import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import { useGT, useLocale } from "gt-next";
import type { Platform, ProfileData, AIAnalysis } from "../types";
import { readFoundProfile } from "../profile-lookup";

/**
 * Avatar image with graceful fallback on error
 */
function AvatarWithFallback(t0) {
  const $ = _c(16);
  const {
    src,
    alt,
    fallbackLetter
  } = t0;
  const [hasError, setHasError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setHasError(false);
      setIsLoaded(false);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== src) {
    t2 = [src];
    $[1] = src;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useEffect(t1, t2);
  if (!src || hasError) {
    let t3;
    if ($[3] !== fallbackLetter) {
      t3 = <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#757575] to-[#141414] text-lg font-bold text-white">{fallbackLetter}</div>;
      $[3] = fallbackLetter;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    return t3;
  }
  let t3;
  if ($[5] !== isLoaded) {
    t3 = !isLoaded && <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-[#8a8a8a] to-[#4a4a4a]" />;
    $[5] = isLoaded;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = `object-cover transition-opacity duration-200 ${isLoaded ? "opacity-100" : "opacity-0"}`;
  let t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setHasError(true);
      setIsLoaded(true);
    };
    t6 = () => setIsLoaded(true);
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  let t7;
  if ($[9] !== alt || $[10] !== src || $[11] !== t4) {
    t7 = <Image src={src} alt={alt} fill={true} className={t4} unoptimized={true} onError={t5} onLoad={t6} />;
    $[9] = alt;
    $[10] = src;
    $[11] = t4;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t3 || $[14] !== t7) {
    t8 = <>{t3}{t7}</>;
    $[13] = t3;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
}
interface LoadingOverlayProps {
  isVisible: boolean;
  username: string;
  platform: Platform;
  /** Pre-fetched avatar URL from search result (optional) */
  avatarUrl?: string | null;
  onComplete: (result: {
    profile: ProfileData | null;
    analysis: AIAnalysis | null;
    error: string | null;
  }) => void;
}

// Loading messages with full context - platform name inserted dynamically
const getLoadingStages = (platform: Platform, username: string, gt: ReturnType<typeof useGT>) => [gt("Connecting to {platform}", {
  platform: platform === "instagram" ? "Instagram" : "TikTok"
}), gt("Fetching @{username}'s profile", {
  username
}), gt("Analyzing your content"), gt("Identifying your niche"), gt("Finding similar creators"), gt("Discovering growth opportunities"), gt("Building your strategy")];

// Minimum time to show each stage (ms)
const STAGE_DURATION = 1800;
// Minimum total loading time (ms) - ensures user sees the animation
const MIN_LOADING_TIME = 4500;

/**
 * Proxy Instagram CDN URL through Cloudflare Workers edge proxy
 */
function getProxiedImageUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (url.includes("cdninstagram.com") || url.includes("instagram.com")) {
    return `https://www.outfame.com/ig-assets/?url=${encodeURIComponent(url)}`;
  }
  return url;
}
export default function LoadingOverlay({
  isVisible,
  username,
  platform,
  avatarUrl: initialAvatarUrl,
  onComplete
}: LoadingOverlayProps) {
  const [currentStage, setCurrentStage] = useState(0);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(initialAvatarUrl || null);
  const startTimeRef = useRef<number>(0);
  const dataReadyRef = useRef<{
    profile: ProfileData | null;
    analysis: AIAnalysis | null;
    error: string | null;
  } | null>(null);
  const hasCompletedRef = useRef(false);

  // gt and locale are stable for the page's life, so the fetch effect below reads them without listing them
  const gt = useGT();
  const locale = useLocale();
  const loadingStages = getLoadingStages(platform, username, gt);

  // Cycle through loading stages
  useEffect(() => {
    if (!isVisible) return;
    const interval = setInterval(() => {
      setCurrentStage(prev => {
        // Loop back to stage 3 after reaching the end (skip the first connection stages)
        if (prev >= loadingStages.length - 1) {
          return 3;
        }
        return prev + 1;
      });
    }, STAGE_DURATION);
    return () => clearInterval(interval);
  }, [isVisible, loadingStages.length]);

  // Reset state when overlay becomes visible
  useEffect(() => {
    if (isVisible) {
      setCurrentStage(0);
      startTimeRef.current = Date.now();
      dataReadyRef.current = null;
      hasCompletedRef.current = false;
      setAvatarUrl(initialAvatarUrl || null);
    }
  }, [isVisible, initialAvatarUrl]);

  // Handle data ready - wait for minimum time then trigger completion
  const handleDataReady = useCallback((result: {
    profile: ProfileData | null;
    analysis: AIAnalysis | null;
    error: string | null;
  }) => {
    if (hasCompletedRef.current) return;
    dataReadyRef.current = result;

    // Update avatar URL from fetched profile
    if (result.profile?.avatarUrl) {
      setAvatarUrl(result.profile.avatarUrl);
    }
    const elapsed = Date.now() - startTimeRef.current;
    const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsed);
    setTimeout(() => {
      if (hasCompletedRef.current) return;
      hasCompletedRef.current = true;

      // CRITICAL: Call onComplete FIRST while overlay is still fully visible
      // This allows parent to change step to 1 BEFORE overlay starts fading
      // The overlay will then fade out to reveal step 1 (not step 0)
      onComplete(result);
    }, remainingTime);
  }, [onComplete]);

  // Fetch data when overlay becomes visible
  useEffect(() => {
    if (!isVisible || !username) return;
    const fetchData = async () => {
      try {
        // Fetch profile (confirmNotFound: a "not found" is re-checked on the backup providers before setup blocks)
        const profileResponse = await fetch(`/api/${platform}/get-profile?username=${encodeURIComponent(username)}&confirmNotFound=true`);
        const rawProfileData = await profileResponse.json();

        // Extract user data from nested structure (Instagram returns graphql.user, TikTok may differ).
        // A `false` body means the account was not found: never let setup continue to payment with it.
        const profileData = profileResponse.ok ? readFoundProfile(rawProfileData) : null;
        if (!profileData) {
          handleDataReady({
            profile: null,
            analysis: null,
            // Server messages are English: other locales get the translated not-found line
            error: locale === "en" ? rawProfileData?.error || `Could not find ${platform} account @${username}` : gt("Could not find {platform} account @{username}", {
              platform: platform === "instagram" ? "Instagram" : "TikTok",
              username
            })
          });
          return;
        }

        // Transform API response to ProfileData format
        const profile: ProfileData = {
          username: profileData.username || username,
          fullName: profileData.full_name || profileData.fullName || null,
          // Use initialAvatarUrl as fallback - it comes from search autocomplete and is known to work
          avatarUrl: profileData.profile_pic_url_hd || profileData.profile_pic_url || profileData.avatar_url || profileData.avatarUrl || initialAvatarUrl || null,
          bio: profileData.biography || profileData.bio || null,
          followerCount: profileData.follower_count || profileData.edge_followed_by?.count || profileData.followers || profileData.followerCount || 0,
          followingCount: profileData.following_count || profileData.edge_follow?.count || profileData.following || profileData.followingCount || 0,
          postCount: profileData.media_count || profileData.edge_owner_to_timeline_media?.count || profileData.posts || profileData.postCount || 0,
          recentPosts: (profileData.edge_owner_to_timeline_media?.edges || profileData.recent_posts || profileData.recentPosts || []).slice(0, 4).map((item: any) => {
            const post = item.node || item;
            return {
              id: post.id || post.pk || String(Math.random()),
              thumbnailUrl: post.thumbnail_url || post.thumbnailUrl || post.display_url || post.thumbnail_src || "",
              caption: post.edge_media_to_caption?.edges?.[0]?.node?.text || post.caption?.text || post.caption || null,
              likeCount: post.edge_liked_by?.count || post.like_count || post.likes || 0,
              commentCount: post.edge_media_to_comment?.count || post.comment_count || post.comments || 0
            };
          }),
          isPrivate: profileData.is_private || profileData.isPrivate || false
        };

        // Fetch AI analysis (non-blocking, can fail gracefully)
        let analysis: AIAnalysis | null = null;
        try {
          const analysisResponse = await fetch(`/api/ai/analyze-account?platform=${platform}&username=${encodeURIComponent(username)}${locale === "en" ? "" : `&locale=${locale}`}`);
          const analysisData = await analysisResponse.json();
          if (analysisResponse.ok && !analysisData.error) {
            analysis = {
              niche: analysisData.niche || "Content Creator",
              nicheTranslations: analysisData.nicheTranslations,
              suggestedCompetitors: analysisData.suggestedCompetitors || [],
              suggestedHashtags: analysisData.suggestedHashtags || [],
              growthPotential: {
                estimatedFollowersPerMonth: analysisData.growthPotential?.estimatedFollowersPerMonth || "1,000-2,000",
                engagementPotential: analysisData.growthPotential?.engagementPotential || "Medium"
              }
            };
          }
        } catch (err_0) {
          console.warn("AI analysis failed, using fallback:", err_0);
        }

        // Fallback analysis if API failed
        if (!analysis) {
          analysis = {
            niche: "Content Creator",
            suggestedCompetitors: [],
            suggestedHashtags: [],
            growthPotential: {
              estimatedFollowersPerMonth: "1,000-2,000",
              engagementPotential: "Medium"
            }
          };
        }
        handleDataReady({
          profile,
          analysis,
          error: null
        });
      } catch (err) {
        console.error("Error fetching data:", err);
        handleDataReady({
          profile: null,
          analysis: null,
          error: gt("Something went wrong. Please try again.")
        });
      }
    };
    fetchData();
  }, [isVisible, username, platform, handleDataReady]);
  const currentMessage = loadingStages[currentStage];
  const proxiedAvatarUrl = getProxiedImageUrl(avatarUrl);
  return <AnimatePresence>
            {isVisible && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.4,
      ease: [0.4, 0, 0.2, 1]
    }} className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white">
                    {/* Center content - logo/avatar + morphing message */}
                    <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.5,
        delay: 0.1
      }} className="flex flex-col items-center">
                        {/* Partnership display - Logo mark + connector + Avatar */}
                        <div className="mb-10 flex items-center gap-3">
                            {/* Outfame logo mark */}
                            <div className="relative h-14 w-14 overflow-hidden rounded-2xl shadow-sm ring-1 ring-black/5">
                                <Image src="/img/outfame-logo-mark.svg" alt="Outfame" fill className="object-cover" priority />
                            </div>

                            {/* Minimal connector icon - partnership style */}
                            <div className="flex items-center justify-center">
                                <svg className="h-5 w-5 text-black/20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M18 8h1a4 4 0 0 1 0 8h-1" />
                                    <path d="M6 8H5a4 4 0 0 0 0 8h1" />
                                    <path d="M8 12h8" />
                                </svg>
                            </div>

                            {/* User avatar - fixed size container with graceful error handling */}
                            <div className="relative h-14 w-14 overflow-hidden rounded-2xl shadow-sm ring-1 ring-black/5">
                                <AvatarWithFallback src={proxiedAvatarUrl} alt={username} fallbackLetter={username.charAt(0).toUpperCase()} />
                            </div>
                        </div>

                        {/* Full contextual message with AnimatePresence */}
                        <div className="relative flex min-h-[48px] items-center justify-center">
                            <AnimatePresence mode="wait">
                                <motion.div key={currentStage} initial={{
              opacity: 0,
              y: 20,
              filter: "blur(8px)"
            }} animate={{
              opacity: 1,
              y: 0,
              filter: "blur(0px)"
            }} exit={{
              opacity: 0,
              y: -20,
              filter: "blur(8px)"
            }} transition={{
              type: "spring",
              stiffness: 300,
              damping: 30
            }} className="absolute whitespace-nowrap">
                                    <span className="text-[24px] font-bold tracking-tight text-ink lg:text-[32px]">{currentMessage}</span>
                                </motion.div>
                            </AnimatePresence>
                        </div>

                        {/* Subtle loading indicator */}
                        <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.4,
          delay: 0.6
        }} className="mt-8 flex justify-center">
                            <LoadingDots />
                        </motion.div>
                    </motion.div>
                </motion.div>}
        </AnimatePresence>;
}

/**
 * Animated loading dots - three dots that pulse in sequence
 */
function LoadingDots() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex items-center gap-1.5">{[0, 1, 2].map(_temp2)}</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp2(index) {
  return <motion.div key={index} className="h-2 w-2 rounded-full bg-ink" animate={{
    opacity: [0.3, 1, 0.3],
    scale: [0.8, 1, 0.8]
  }} transition={{
    duration: 1.2,
    repeat: Infinity,
    delay: index * 0.2,
    ease: "easeInOut"
  }} />;
}